import {
  IResetPassword,
  isEmpty,
  openNotification,
  resetPassword,
  useDocumentTitle,
} from "@dataone/lib";
import { useMutation } from "@tanstack/react-query";
import { Form, Button, Input } from "antd";
import { useEffect, useState } from "react";
import { styled } from "styled-components";
import { withAuth } from "../../auth/helpers/hoc";

const PasswordPage = () => {
  const [resetPassForm] = Form.useForm();
  const { isLoading, mutate: resetPsd } = useMutation(resetPassword);
  useDocumentTitle("OneGate | Password Settings");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isChanged, setIsChanged] = useState<boolean>(false);

  useEffect(() => {
    if (!isEmpty(errorMessage)) {
      resetPassForm.validateFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMessage, isChanged]);

  const handleChangeConfirmPassword = () => {
    if (isChanged === true) {
      setIsChanged(false);
    }
  };
  const handelCurrentPassword = () => {
    if (
      resetPassForm.getFieldValue("newPassword") !== "" &&
      resetPassForm.getFieldValue("oldPassword") !== "" &&
      resetPassForm.getFieldValue("oldPassword") ===
        resetPassForm.getFieldValue("newPassword")
    ) {
      resetPassForm.setFields([
        {
          name: "newPassword",
          errors: [
            "The new password cannot be the same as the current password",
          ],
        },
      ]);
    } else {
      resetPassForm.setFields([
        {
          name: "newPassword",
          errors: [],
        },
      ]);
    }
  };
  const onFinish = (values: IResetPassword) => {
    values.confirmNewPassword === values.newPassword
      ? resetPsd(
          { newPassword: values.newPassword, oldPassword: values.oldPassword },
          {
            onSuccess: (data) => {
              resetPassForm.resetFields();
              openNotification(data);
            },
            onError: (error: any) => {
              setErrorMessage(error?.response?.data?.message);
            },
          }
        )
      : setIsChanged(true);
    setErrorMessage("Password confirmation and new password do not match");
  };

  return (
    <StyledPasswordPage>
      <h2>Password Settings</h2>
      <StyledContent>
        <StyledDescription>
          <div style={{ maxWidth: "300px", fontSize: "13px" }}>
            Your new password must be different from previous passwords
          </div>
        </StyledDescription>
        <StyledForm>
          <Form form={resetPassForm} onFinish={onFinish} name="resetPassword">
            <div className="form-inputs">
              <Form.Item
                name="oldPassword"
                rules={[
                  {
                    required: true,
                    message: "Please input your current password!",
                  },
                ]}
              >
                <StyledPasswordInput
                  size="large"
                  placeholder="Enter your current password"
                  maxLength={255}
                  onChange={handelCurrentPassword}
                />
              </Form.Item>
              <Form.Item
                name="newPassword"
                rules={[
                  {
                    required: true,
                    message: "Please input your New Password!",
                  },
                  {
                    message: "Password must be 8 or more characters in length.",
                  },
                  {
                    validator: (_, value) => {
                      if (
                        isEmpty(value) &&
                        isEmpty(resetPassForm.getFieldValue("oldPassword"))
                      )
                        return Promise.reject("");
                      if (
                        value === resetPassForm.getFieldValue("oldPassword")
                      ) {
                        return Promise.reject(
                          "The new password cannot be the same as the current password"
                        );
                      }
                      if (value && value.length < 8) {
                        return Promise.reject(
                          new Error(
                            "Password must be at least 8 characters long"
                          )
                        );
                      }
                      if (value && !/[A-Z]/.test(value)) {
                        return Promise.reject(
                          new Error(
                            "Password must contain at least one uppercase letter"
                          )
                        );
                      }
                      if (value && !/[a-z]/.test(value)) {
                        return Promise.reject(
                          new Error(
                            "Password must contain at least one lowercase letter"
                          )
                        );
                      }
                      if (value && !/[-+_!@#$%^&*,.?]/.test(value)) {
                        return Promise.reject(
                          new Error("Password must contain at least one symbol")
                        );
                      }
                      if (value && !/[0-9]/.test(value)) {
                        return Promise.reject(
                          new Error("Password must contain at least one digit")
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <StyledPasswordInput
                  size="large"
                  placeholder="Enter your New Password"
                  maxLength={16}
                />
              </Form.Item>
              <Form.Item
                name="confirmNewPassword"
                rules={[
                  { required: true, message: "Please confirm new password!" },
                  {
                    validator: async () => {
                      if (!isEmpty(errorMessage) && isChanged)
                        return Promise.reject(new Error(errorMessage));
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <StyledPasswordInput
                  size="large"
                  placeholder="Enter your new password confirmation"
                  maxLength={16}
                  onChange={() => handleChangeConfirmPassword()}
                />
              </Form.Item>
            </div>
            <Form.Item style={{ textAlign: "center", marginTop: "-5px" }}>
              <StyledButton size="large" htmlType="submit" disabled={isLoading}>
                {isLoading ? "Changing password ..." : "Change password"}
              </StyledButton>
            </Form.Item>
          </Form>
        </StyledForm>
      </StyledContent>
    </StyledPasswordPage>
  );
};

export default withAuth(PasswordPage);

const StyledForm = styled.div`
  width: 100%;
  .form-inputs {
    padding: 0px 80px;
  }
  @media screen and (max-width: 1200px) {
    .form-inputs {
      padding: 0px;
    }
  }
`;

const StyledDescription = styled.div`
  width: 35vw;
`;

const StyledContent = styled.div`
  align-items: baseline;
  display: flex;
  flex-direction: column;
  gap: 3rem;

  .ant-form-item-explain-error {
    font-size: 12px !important;
  }
`;

const StyledPasswordPage = styled.div`
  width: 100%;
  padding: 15px 0px 0px 30px;
  h2 {
    color: #4974f6;
    font-weight: 700;
    font-size: 18px;
    margin-top: 2px;
    margin-bottom: 40px;
  }

  .ant-breadcrumb li:nth-of-type(1) a {
    font-size: 16px !important;
    color: #040726 !important;
    font-weight: 200 !important;
  }

  .ant-breadcrumb a {
    color: #4974f6;
    transition: color 0.2s;
    padding: 0 4px;
    border-radius: 4px;
    height: 22px;
    display: inline-block;
    margin-inline: -4px;
    font-size: 18px !important;
    font-weight: bold;
    background-color: transparent;
  }
  .ant-form-item .ant-form-item-control-input-content {
    padding: 0px 33px;
  }
`;

const StyledButton = styled(Button)`
  background-color: #4974f6;
  color: #ffffff;
  border: none;
  float: right;
  margin-top: 15px;
  font-size: 14px !important;
  height: 35px !important;
  &:hover {
    background: #4974f6 !important;
    color: #ffffff !important;
  }
`;

const StyledPasswordInput = styled(Input.Password)`
  border: none;
  font-size: 12px;
  margin-top: 10px;
  background: #eff1fd !important;
  padding: 3px 10px;
  .ant-input {
    background: #eff1fd !important;
    height: 30px !important;
  }
`;
